// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DataSourceGridAdvancedActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React from 'react';
import m from 'mobx';
import 'share/utils/Izenda.StringUtilities';
import AppGlobal from 'Izenda.AppGlobal';
import EventUtils from 'EventUtils';
import Validator from 'share/components/hoc/Izenda.Validator';
import DefaultInput from 'share/components/controls/Izenda.Input';
import DefaultDropDown from 'share/components/controls/Izenda.DropDown';
import DefaultDateTimePicker from 'share/components/controls/DateTime/Izenda.DateTimePicker';
import GridActionColumn from './Izenda.GridActionColumn';
import ReportDesignerCategoryDropdown from './Izenda.ReportDesignerCategoryDropdown';
import ReportDesignerDataObjectDropdown from './Izenda.ReportDesignerDataObjectDropdown';
import ReportDesignerJoinFieldDropdown from './Izenda.ReportDesignerJoinFieldDropdown';
import KeyJoinComparisonValue from './Izenda.KeyJoinComparisonValue';

import { withHandlers } from 'recompose';
import {
  curry,
  isEmpty,
  isNil,
  reject,
  first,
  get,
  concat,
  flatMap,
  flow,
  map,
  split,
  filter,
  find,
  reduce,
  includes,
  trim,
  clone,
  head,
  remove,
  some,
  uniq,
  startsWith,
  toUpper
} from 'lodash/fp';
import constants, { VALIDATION_KEY, EMPTY_GUID } from 'logic/data/Izenda.ReportDesignerConstants';
import localizer from 'share/localization/Izenda.Localizer';

const Input = Validator()(DefaultInput);
const DropDown = Validator()(DefaultDropDown);
const DateTimePicker = Validator()(DefaultDateTimePicker);
const FOREIGN_DATA_OBJECT_KEY = constants.TABLE_DEF.ADVANCED_MODE.foreignDataObject.key;
const CRITERIA_LIST_ADVANCED_MODE = constants.BUNDLE.CRITERIA_LIST_ADVANCED_MODE;

/**
 * Build drop-down component for data object.
 * @param {any} dataSourceItem
 * @param {string} key
 * @param {string} categoryKey
 * @param {any} store
 * @param {number} rowIndex
 * @param {any} column
 * @param {any} valManager
 */
function getDropdownForDataObject(
  dataSourceItem: any,
  key: string,
  categoryKey: string,
  store: any,
  rowIndex: number,
  column: any,
  valManager: any
) {
  const isKeyJoin = dataSourceItem.isKeyJoin === true;
  const isForeignDataObject = key === constants.TABLE_DEF.ADVANCED_MODE.foreignDataObject.key;
  const comparisonType = findComparisonType(get('comparisonOperator', dataSourceItem));
  let dataObjects = getSelectedDataObjects(dataSourceItem, categoryKey, store, isKeyJoin);

  // Return Empty cell if comparison type != Field
  if (comparisonType !== constants.COMPARISION_TYPE.FIELD && isForeignDataObject) {
    return null;
  }

  // Concat option with invalidAlias
  if (dataSourceItem.invalidAlias) {
    dataSourceItem.invalidAlias.alias.id = dataSourceItem.invalidAlias.alias.aliasId;
    dataObjects = concat(dataSourceItem.invalidAlias.alias, dataObjects);
  }

  // Filter data object for key join
  if (isKeyJoin) {
    dataObjects = filterDataObjectForKeyJoin(dataSourceItem, dataObjects, store);
  }

  const useAliasId = isForeignDataObject || isKeyJoin === true;
  let options = getDropdownOptions(dataObjects, true, useAliasId);
  let dataTree = store.dataTree;
  let dataSourceDetail = store.dataSourceDetail;

  return (
    <ReportDesignerDataObjectDropdown
      options={options}
      dataObjects={dataObjects}
      dataSourceItem={dataSourceItem}
      dataTree={dataTree}
      dataSourceDetail={dataSourceDetail}
      fieldName={key}
      rowIndex={rowIndex}
      checkJoinTypeEntities={checkJoinTypeEntities}
      store={store}
    />
  );
}

/**
 * Filter data object value for key join.
 * @param {any} dataSourceItem
 * @param {array} dataObjects
 * @param {any} store
 */
function filterDataObjectForKeyJoin(dataSourceItem: any, dataObjects: Array<any>, store: any) {
  const upperRelationships = findUpperRelationships(dataSourceItem, store);

  const upperDataObjectKeys = concat(
    flatMap('joinQuerySourceId', upperRelationships),
    flow(
      flatMap('selectedForeignAlias'),
      map(selectedAlias =>
        flow(
          split('_'),
          head
        )(selectedAlias)
      )
    )(upperRelationships)
  );

  return filter(dataObject => upperDataObjectKeys.indexOf(dataObject.id) > -1, dataObjects);
}

/**
 * Find relationships which level is lesser than level of specify item's relationship.
 * @param {any} dataSourceItem
 * @param {any} store
 */
function findUpperRelationships(dataSourceItem: any, store: any) {
  const currentRelationship = find({ tempId: dataSourceItem.parentId }, store.dataSourceDetail.dataSourceGrid);

  if (isEmpty(currentRelationship)) {
    return [];
  }

  const upperRelationships = filter(
    item => item.level <= currentRelationship.level,
    store.dataSourceDetail.dataSourceGrid
  );

  return upperRelationships;
}

/**
 * Build drop-down component for category
 * @param {any} dataSourceItem
 * @param {string} key
 * @param {any} store
 * @param {number} rowIndex
 */
function getDropdownForCategory(dataSourceItem: any, key: string, store: any, rowIndex: number) {
  const categoryOptions =
    dataSourceItem.isKeyJoin === true
      ? getCategoryForKeyJoin(store, dataSourceItem)
      : getCategoryForRelationship(store);

  return (
    <ReportDesignerCategoryDropdown
      dataSourceItem={dataSourceItem}
      categoryOptions={categoryOptions}
      fieldName={key}
      rowIndex={rowIndex}
      dataSourceDetail={store.dataSourceDetail}
    />
  );
}

/**
 * Get categories for relationship.
 * @param {any} store
 */
function getCategoryForRelationship(store: any) {
  const {
    dataTree: { selectedTree }
  } = store;

  return flow(
    filter(category => some('selected', category.querySource)),
    map(category => {
      return {
        label: category.name || constants.UNCATEGORIZED,
        value: category.id
      };
    })
  )(selectedTree);
}

/**
 * Get categories for key join.
 * @param {any} store
 * @param {any} dataSourceItem
 */
function getCategoryForKeyJoin(store: any, dataSourceItem: any) {
  const upperRelationships = findUpperRelationships(dataSourceItem, store);

  const relationshipCategoryKeys = reduce(
    (result, relationship) => {
      const readyCategory =
        relationship.joinDataSourceCategoryId && !includes(relationship.joinDataSourceCategoryId, result);
      readyCategory && result.push(relationship.joinDataSourceCategoryId);

      const readyForeignCategory =
        relationship.foreignDataSourceCategoryId && !includes(relationship.foreignDataSourceCategoryId, result);
      readyForeignCategory && result.push(relationship.foreignDataSourceCategoryId);
      return result;
    },
    [],
    upperRelationships
  );

  // Get categories for key join, remove category which is not in higher order than key join's parent
  return remove(item => !includes(item.value, relationshipCategoryKeys), getCategoryForRelationship(store));
}

/**
 * Collect data object and foreign data object
 * @param  {array} upperRelationships are relationship created higher level
 * @return {array} the list objects data object
 */
function buildKeyJoinList(upperRelationships: Array<any>) {
  const objects = reduce(
    (result, relationship) => {
      const readyDataObject =
        relationship.joinQuerySourceId &&
        isEmpty(
          find(
            {
              aliasId: relationship.joinQuerySourceId + '_' + trim(relationship.joinQuerySourceName)
            },
            result
          )
        );

      readyDataObject &&
        result.push({
          aliasId: relationship.joinQuerySourceId + '_' + trim(relationship.joinQuerySourceName),
          name: trim(relationship.joinQuerySourceName)
        });

      const readyForeignAlias =
        relationship.selectedForeignAlias &&
        isEmpty(find({ aliasId: trim(relationship.selectedForeignAlias) }, result));

      readyForeignAlias &&
        result.push({
          aliasId: trim(relationship.selectedForeignAlias),
          name: trim(relationship.foreignQuerySourceName)
        });

      return result;
    },
    [],
    upperRelationships
  );

  return objects;
}

/**
 * Transform a list selected data included aliases to data object
 * @param  {array} objects            list object will be transformed
 * @param  {string} dataSourceItemKey  property name
 * @param  {array} selectedCategories category selected for current keyjoin
 * @return {array}                    List data object are ready for the keyjoin
 */
function transformKeyJoinToDataObject(objects: Array<any>, dataSourceItemKey: string, selectedCategories: Array<any>) {
  const dataObjects = reduce(
    (result, item) => {
      const dataObject = find({ aliasId: item.aliasId }, selectedCategories[dataSourceItemKey].dataObjects);

      // if new alias dataObject generated then that doesn't exist in the selected categories.
      // So we need to find the source dataObject and clone and push to keyjoin list
      if (isNil(dataObject)) {
        const id = item.aliasId && item.aliasId.substring(0, 36);
        const dataObject = find({ id: id }, selectedCategories[dataSourceItemKey].dataObjects);

        if (!isNil(dataObject)) {
          let aliasObject = clone(dataObject);
          aliasObject.name = item.name;
          aliasObject.aliasId = item.aliasId;
          result.push(aliasObject);
        }
      } else {
        result.push(dataObject);
      }
      return result;
    },
    [],
    objects
  );

  return dataObjects;
}

/**
 * Build list data object for key join
 * @param  {any} dataSourceItem    current keyjoin
 * @param  {string} categoryKey    category property name
 * @param  {any} store             the main store
 * @return {array}                 return list data object
 */
function getKeyJoinDataObjects(dataSourceItem: any, categoryKey: string, store: any) {
  const dataTree = store.dataTree;
  const selectedCategories = dataTree.selectedCategories;
  const dataSourceItemKey = dataSourceItem[categoryKey];

  if (isEmpty(dataSourceItemKey)) {
    return [];
  }

  const currentRelationship = find({ tempId: dataSourceItem.parentId }, store.dataSourceDetail.dataSourceGrid);

  if (isEmpty(currentRelationship)) {
    return [];
  }

  const upperRelationships = filter(
    item => item.level <= currentRelationship.level,
    store.dataSourceDetail.dataSourceGrid
  );

  const objects = buildKeyJoinList(upperRelationships);
  const dataObjects = transformKeyJoinToDataObject(objects, dataSourceItemKey, selectedCategories);
  return dataObjects;
}

/**
 * Get selected data objects.
 * @param {any} dataSourceItem
 * @param {string} categoryKey
 * @param {any} store
 * @param {boolean} isKeyJoin
 */
function getSelectedDataObjects(dataSourceItem: any, categoryKey: string, store: any, isKeyJoin: boolean) {
  const {
    dataTree: { selectedTree }
  } = store;
  let dataSourceItemKey = dataSourceItem[categoryKey];

  const category = find({ id: dataSourceItemKey }, selectedTree);
  let dataObjectsBelongToCategory = [];
  if (category) {
    dataObjectsBelongToCategory = filter({ selected: true }, category.querySource);
  }

  if (isEmpty(dataSourceItemKey)) {
    return [];
  }

  let availableAlias = [];
  if (categoryKey === constants.TABLE_DEF.ADVANCED_MODE.foreignCategory.key || isKeyJoin) {
    availableAlias = reduce(
      (result, aliasDetail) => {
        const dataObject = find({ id: aliasDetail.alias.sourceId }, dataObjectsBelongToCategory);
        if (!isNil(dataObject)) {
          const aliasObject = m.toJS(dataObject);
          aliasObject.name = trim(aliasDetail.alias.name);
          aliasObject.aliasId = trim(aliasDetail.alias.aliasId);
          aliasObject.isDataSourceAlias = true;

          const notInAliasCollection = isEmpty(find({ aliasId: trim(aliasObject.aliasId) }, result));
          const notDataObjectList = isEmpty(find({ aliasId: trim(aliasObject.aliasId) }, dataObjectsBelongToCategory));
          if (notInAliasCollection && notDataObjectList) {
            result.push(aliasObject);
          }
        }
        return result;
      },
      [],
      store.dataSourceDetail.availableAlias(dataSourceItem)
    );
  }

  return !isEmpty(dataObjectsBelongToCategory) ? concat(availableAlias, dataObjectsBelongToCategory) : [];
}

/**
 * get options for Dropdown list
 * @param {array} list
 * @param {boolean} isNotRemoveItem
 * @param {boolean} useAliasId
 */
function getDropdownOptions(list: Array<any>, isNotRemoveItem: boolean, useAliasId: boolean) {
  let options = [];
  const showSchemaName = AppGlobal.advancedSettings.other.showSchemaNameValue;
  const newList = flow(
    filter(item => (item.visible || isNotRemoveItem) && !item.isCalculated),
    map(item => {
      return {
        label: item.name,
        value: !!useAliasId ? item.aliasId : item.id,
        visible: item.visible,
        isDataSourceAlias: item.isDataSourceAlias,
        labelDisplay:
          showSchemaName && !item.isDataSourceAlias && item.querySourceCategoryName
            ? item.querySourceCategoryName + '.' + item.name
            : item.name
      };
    })
  )(list);

  options.splice.apply(options, [1, 0].concat(newList));
  return options;
}

/**
 * Create dropdown list for field of Data Object and Foreign Data object
 * @param  {any} dataSourceItem current relationship item
 * @param  {any} column
 * @param  {string} dataObjectKey  dataObject field
 * @param  {string} categoryKey    category field of relationship item
 * @param  {any} store
 * @param  {number} rowIndex
 * @param  {any} valManager
 * @param  {boolean} isForeignJoinField
 */
function getDropdownForJoinField(
  dataSourceItem: any,
  column: any,
  dataObjectKey: string,
  categoryKey: string,
  store: any,
  rowIndex: number,
  valManager: any,
  isForeignJoinField: boolean
) {
  const comparisonType = findComparisonType(get('comparisonOperator', dataSourceItem));

  // Return Empty cell if comparison type != Field
  if (comparisonType !== constants.COMPARISION_TYPE.FIELD && isForeignJoinField) {
    return null;
  }

  function changeJoinField() {
    store.reportStore.model.markDirty();
    store.reportStore.dataSourceStore.dataSourceDetail.markDataSourceGridDirty();
    valManager.reset();
  }

  return (
    <ReportDesignerJoinFieldDropdown
      dataSourceItem={dataSourceItem}
      dataObjectKey={dataObjectKey}
      categoryKey={categoryKey}
      getDataObjects={getSelectedDataObjects}
      getDropdownOptions={getDropdownOptions}
      store={store}
      onChange={changeJoinField}
      rowIndex={rowIndex}
      column={column}
    />
  );
}

/**
 * Verify join type entities
 * @param {any} store
 * @param {any} item
 */
function checkJoinTypeEntities(props: any, store: any, item: any) {
  const { dataSourceDetail } = store;
  const existedItem = dataSourceDetail.checkExistEntities.call(dataSourceDetail, item);

  if (existedItem) {
    const message = String.format(
      constants.BUNDLE.MESSAGE.CHANGE_JOIN_TYPE,
      existedItem.joinQuerySourceName,
      existedItem.foreignQuerySourceName
    );

    return props
      .confirmFn('Confirmation', {
        description: message
      })
      .then(() => {
        dataSourceDetail.updateJoinTypeForSameEntities.call(dataSourceDetail, item, true);
        return true;
      })
      .catch(() => {
        dataSourceDetail.updateJoinTypeForSameEntities.call(dataSourceDetail, item, false);
        return false;
      });
  }

  return Promise.resolve(true);
}

/**
 * Find Comparison Type
 * @param {string} comparisonOperator
 * @returns {string} comparison type
 */
function findComparisonType(comparisonOperator: string) {
  return flow(
    find({ value: comparisonOperator }),
    get('type')
  )(constants.COMPARISION_OPTIONS);
}

/**
 * Use multi value comparison
 * @param {string} comparisonOperator
 */
function useMultiValueComparison(comparisonOperator: string) {
  return startsWith('=', comparisonOperator) || startsWith('<>', comparisonOperator);
}

/**
 * Get Comparison Value control, when comparison operator != Field group
 * @param {any} dataSourceItem
 * @param {any} store
 */
function getComparisonValue(dataSourceItem: any, store: any, valManager: any) {
  const COMPARISION_TYPE = constants.COMPARISION_TYPE;
  const comparisonOperator = get('comparisonOperator', dataSourceItem);
  const comparisonType = findComparisonType(comparisonOperator);
  const multi = useMultiValueComparison(comparisonOperator);

  const commonProps = {
    value: dataSourceItem.comparisonValue,
    field: 'comparisonValue',
    group: 'Relationships' + dataSourceItem.tempId
  };

  const joinField = getJoinField(dataSourceItem, store);

  function changeComparisionValue(value) {
    let currentValues = split(';#', value);
    value = uniq(currentValues).join(';#');

    dataSourceItem.setComparisionValue && dataSourceItem.setComparisionValue(value);
    store.dataSourceDetail.markDirty();
    store.dataSourceDetail.markDataSourceGridDirty();
  }

  switch (comparisonType) {
    case COMPARISION_TYPE.VALUE:
      return (
        <KeyJoinComparisonValue
          {...commonProps}
          changeComparisionValue={changeComparisionValue}
          joinField={joinField}
          multi={multi}
        />
      );

    case COMPARISION_TYPE.TIME:
    case COMPARISION_TYPE.DATE:
      return (
        <DateTimePicker
          multi={multi}
          {...commonProps}
          time={comparisonType !== COMPARISION_TYPE.DATE}
          calendar={comparisonType !== COMPARISION_TYPE.TIME}
          onChange={changeComparisionValue}
        />
      );
    case COMPARISION_TYPE.DATE_TIME:
      return (
        <DateTimePicker
          multi={multi}
          combine={multi}
          {...commonProps}
          time={comparisonType !== COMPARISION_TYPE.DATE}
          calendar={comparisonType !== COMPARISION_TYPE.TIME}
          onChange={changeComparisionValue}
        />
      );
  }

  return null;
}

/**
 * Get join field
 * @param {any} dataSourceItem
 * @param {any} store
 */
function getJoinField(dataSourceItem: any, store: any) {
  const selectedDataObjects = getSelectedDataObjects(
    dataSourceItem,
    constants.TABLE_DEF.ADVANCED_MODE.category.key,
    store
  );

  return flow(
    flatMap(querySource => querySource.fields.slice()),
    find({ id: dataSourceItem.joinFieldId })
  )(selectedDataObjects);
}

export default withHandlers({
  /**
   * Occur when a relationship is added
   */
  onAddRelationship: curry((props: any, _) => {
    const { store } = props;
    const {
      dataSourceDetail,
      dataTree: { selectedTree },
      reportStore,
      isAllQuerySourceUncategorized
    } = store;

    dataSourceDetail.addNew(selectedTree, isAllQuerySourceUncategorized);
    reportStore.model.markDirty();
  }),

  /**
   * Occur when click validate syntax button
   */
  onValidateSyntax: curry(async(props: any, _) => {
    const {
      store: { reportStore },
      validation,
      localization
    } = props;

    reportStore.alertDetails.reset();
    const validateResult = await validation.validate();
    if (validateResult) {
      const [validateSyntaxResult, validateCycleResult] = [
        await reportStore.validateRelationshipSyntax(),
        await reportStore.validateCycleRelationships()
      ];

      if (validateSyntaxResult.success && validateCycleResult.success) {
        reportStore.setAlertDetails({
          success: true,
          message: localization.getText('REPORTDESIGNER_MESSAGE_VALID_SQL_STATEMENT')
        });
      } else {
        !validateSyntaxResult.success && reportStore.model.updateErrorMessages(validateSyntaxResult.messages);
        !validateCycleResult.success &&
          reportStore.setAlertDetails({
            success: false,
            errors: [{ message: get('messages[0].messages[0]', validateCycleResult) }]
          });
      }
    }

    return validateResult;
  }),

  /**
   * Handle moving relationship between rows:
   * 1. Move rows in dataSourceGrid
   * 2. Reindex AliasLevel
   * 3. Handle invalid aliases
   * @param {number} currentIndex
   * @param {number} newIndex
   */
  onMoveRelationship: curry((props: any, currentIndex: number, newIndex: number, currentRow: any, newRow: any) => {
    if (isNil(currentRow) || isNil(newRow) || currentRow === newRow) {
      return;
    }

    const { store, validation, localization } = props;
    const { dataSourceDetail } = store;
    const { dataSourceGrid } = dataSourceDetail;

    validation.reset();

    const handleInvalidAlias = (item: any) => {
      const invalidAliasLevels = dataSourceDetail.getInvalidAliasLevelsForRow(item);

      // Only one invalid Alias at same time
      item.invalidAlias = invalidAliasLevels.length > 0 ? first(invalidAliasLevels) : null;

      if (invalidAliasLevels > 0) {
        const message = item.isKeyJoin
          ? localization.getText('COMMON_MESSAGES_INVALID_KEY_JOIN')
          : localization.getText('COMMON_MESSAGES_INVALID_RELATIONSHIP');

        validation.updateErrors([
          {
            key: `Relationships${item.validationKey}${FOREIGN_DATA_OBJECT_KEY}`,
            message: [message]
          }
        ]);
      }
    };

    dataSourceDetail.moveRelationshipOrKeyJoin(currentRow, newRow, VALIDATION_KEY).then(() => {
      dataSourceGrid.map(item => {
        handleInvalidAlias(item);
      });

      store.reportStore.model.markDirty();
    });
  }),

  /**
   * Rebuild search options
   */
  rebuildSearchOptions: curry((props: any, _) => {
    const { isAllQuerySourceUncategorized } = props.store;

    return !isAllQuerySourceUncategorized
      ? CRITERIA_LIST_ADVANCED_MODE
      : reject({ label: 'Category' }, CRITERIA_LIST_ADVANCED_MODE);
  }),

  /**
   * Get childs item
   */
  getChildItems: curry((props: any, item: any) => {
    return item.displayKeyJoins;
  }),

  renderAlias: curry(({ validation: valManager, store }, dataSourceItem: any, key: string) => {
    if (isNil(dataSourceItem[key])) {
      return '';
    }
    const {
      dataSourceDetail,
      reportStore: { model: dataModel }
    } = store;
    const dataObjectKey = constants.TABLE_DEF.ADVANCED_MODE.dataObject.key;

    function aliasTextChange(e) {
      valManager.reset();
      dataSourceItem.setAlias(e.target.value);
      dataModel.markDirty();
      dataSourceDetail.markDataSourceGridDirty();
    }

    const aliasUsed = (alias, joinQuerySourceId, dataSourceGrid) => {
      return some(relationshipDetail => {
        return relationshipDetail.selectedForeignAlias === `${joinQuerySourceId}_${alias}`
          || some(keyJoinDetail => keyJoinDetail.foreignQuerySourceName === alias
              || keyJoinDetail.joinQuerySourceName === alias, relationshipDetail.relationshipKeyJoins);
      }, dataSourceGrid);
    };

    const blurAlias = e => {
      const value = e.target.value;

      // Revert to previous alias if current value is blank, data source needs alias, and joinQuerySourceId has value.
      if (!value && dataSourceItem.needAlias && dataSourceItem[dataObjectKey]
        && aliasUsed(dataSourceItem.previousAlias, dataSourceItem.joinQuerySourceId, dataSourceDetail.dataSourceGrid)) {
        dataSourceItem.alias = dataSourceItem.previousAlias;
      }

      // Handle for manually input alias
      if (value && !dataSourceItem.needAlias) {
        dataSourceItem.needAlias = true;
      }

      if (dataSourceItem.alias !== dataSourceItem.previousAlias) {
        dataSourceDetail.populateUpdatedAlias(dataSourceItem);
      }

      dataSourceDetail.updateAliasLevel(
        dataSourceDetail.getAliasOption(dataSourceItem, dataSourceItem[key]),
        dataSourceItem
      );
    };

    function focusAlias() {
      if (dataSourceItem.needAlias) {
        dataSourceItem.previousAlias = dataSourceItem.alias;
      }
    }

    return (
      <Input
        className='form-control col-md-12'
        containerClassName=''
        value={dataSourceItem[key]}
        maxLength='100'
        onChange={aliasTextChange}
        onFocus={focusAlias}
        onBlur={blurAlias}
        field={key}
        group={'Relationships' + dataSourceItem.tempId}
      />
    );
  }),

  renderJoinType: curry((props: any, dataSourceItem: any, key: string, rowIndex: number, column: any) => {
    const {
      store,
      store: { reportStore },
      validation: valManager,
      confirmFn
    } = props;

    if (isNil(dataSourceItem[key])) {
      return '';
    }

    const checkJoinType = object => {
      checkJoinTypeEntities(props, store, dataSourceItem).then(result => {
        if (result && object.value === constants.JOIN_TYPE_CROSS) {
          dataSourceItem.joinFieldId = null;
          dataSourceItem.foreignFieldId = null;
        }
      });
    };

    const changeJoinType = object => {
      valManager.reset();
      reportStore.model.markDirty();
      reportStore.dataSourceStore.dataSourceDetail.markDataSourceGridDirty();

      // Clear key join when user select Cross
      if (object.value === constants.JOIN_TYPE_CROSS && !isEmpty(dataSourceItem.relationshipKeyJoins)) {
        confirmFn('Confirmation', {
          description: constants.BUNDLE.MESSAGE.SELECT_CROSS_JOIN
        }).then(function() {
          dataSourceItem.updateJoinType(key, object.value);
          checkJoinType(object);
        });
      } else {
        dataSourceItem.updateJoinType(key, object.value);
        checkJoinType(object);
      }
    };

    return (
      <DropDown
        options={constants.JOIN_TYPES}
        onChange={changeJoinType}
        value={dataSourceItem[key]}
        valueField='value'
        labelField='label'
        validations={constants.REQUIRED_CONSTRAINT}
        field={key}
        group={'Relationships' + dataSourceItem.tempId}
      />
    );
  }),

  renderOperator: curry(
    (
      { validation: valManager, store: { reportStore } },
      dataSourceItem: any,
      key: string,
      rowIndex: number,
      column: any
    ) => {
      if (isNil(dataSourceItem[key])) {
        return '';
      }

      const changeOperator = object => {
        valManager.reset();
        dataSourceItem.updateKeyJoinItem(key, object.value);
        reportStore.model.markDirty();
        reportStore.dataSourceStore.dataSourceDetail.markDataSourceGridDirty();
      };

      const operatorValue = toUpper(dataSourceItem[key]);

      return (
        <DropDown
          options={constants.OPERATOR_TYPES}
          onChange={changeOperator}
          value={operatorValue}
          valueField='value'
          labelField='label'
          validations={constants.REQUIRED_CONSTRAINT}
          field={key}
          group={'Relationships' + dataSourceItem.tempId}
        />
      );
    }
  ),

  renderCategory: curry(
    ({ store, validation: valManager }, dataSourceItem: any, key: string, rowIndex: number, column: any) => {
      const isForeignCategory = key === constants.TABLE_DEF.ADVANCED_MODE.foreignCategory.key;
      const comparisonType = findComparisonType(get('comparisonOperator', dataSourceItem));
      const { isAllQuerySourceUncategorized } = store;

      if (!isAllQuerySourceUncategorized && isForeignCategory && comparisonType !== constants.COMPARISION_TYPE.FIELD) {
        return getComparisonValue(dataSourceItem, store, valManager);
      }

      return getDropdownForCategory(dataSourceItem, key, store, rowIndex);
    }
  ),

  renderDataObject: curry(
    ({ validation: valManager, store }, dataSourceItem: any, key: string, rowIndex: number, column: any) => {
      const categoryKey = constants.TABLE_DEF.ADVANCED_MODE.category.key;
      return getDropdownForDataObject(dataSourceItem, key, categoryKey, store, rowIndex, valManager);
    }
  ),

  renderForeignDataObject: curry(
    (
      {
        validation: valManager,
        store,
        store: {
          dataTree: { isAllDataOjectUncategory: needShowComparisonValueInDataObjectCol }
        }
      },
      dataSourceItem: any,
      key: string,
      rowIndex: number,
      column: any
    ) => {
      const categoryKey = constants.TABLE_DEF.ADVANCED_MODE.foreignCategory.key;
      const comparisonType = findComparisonType(get('comparisonOperator', dataSourceItem));

      // The category column will be hidden if all data objects are uncategorized.
      // So it needs to show comparison value in here if the category column is hidden.
      // It only show comparison value in either category column (if showing) or foreign data object column, not both.
      if (needShowComparisonValueInDataObjectCol && comparisonType !== constants.COMPARISION_TYPE.FIELD) {
        return getComparisonValue(dataSourceItem, store, valManager);
      }

      return getDropdownForDataObject(dataSourceItem, key, categoryKey, store, rowIndex, valManager);
    }
  ),

  renderJoinField: curry(
    ({ validation: valManager, store }, dataSourceItem: any, key: string, rowIndex: number, column: any) => {
      const dataObjectKey = constants.TABLE_DEF.ADVANCED_MODE.dataObject.key;
      const categoryKey = constants.TABLE_DEF.ADVANCED_MODE.category.key;
      return getDropdownForJoinField(dataSourceItem, column, dataObjectKey, categoryKey, store, rowIndex, valManager);
    }
  ),

  /**
   * Render foreign join field. Also this column can render filter alias (because
   * currently we don't have separate column for it).
   */
  renderForeignJoinField: curry(
    ({ validation: valManager, store }, dataSourceItem: any, key: string, rowIndex: number, column: any) => {
      const {
        dataSourceDetail,
        reportStore: { model: dataModel }
      } = store;

      if (dataSourceItem.isFilterKeyJoin) {
        return <Input
          className='form-control col-md-12'
          containerClassName=''
          value={dataSourceItem['foreignFilterAlias']}
          maxLength='100'
          onChange={EventUtils.notifyChange(onChangeForeignFilterAlias, EventUtils.INPUT)}
          field={key}
          group={'Relationships' + dataSourceItem.tempId}
        />;
      }

      const dataObjectKey = constants.TABLE_DEF.ADVANCED_MODE.foreignDataObject.key;
      const categoryKey = constants.TABLE_DEF.ADVANCED_MODE.foreignCategory.key;
      return getDropdownForJoinField(
        dataSourceItem,
        column,
        dataObjectKey,
        categoryKey,
        store,
        rowIndex,
        valManager,
        true
      );

      function onChangeForeignFilterAlias(value) {
        valManager.reset();
        dataSourceItem.setForeignFilterAlias(value);
        dataModel.markDirty();
        dataSourceDetail.markDataSourceGridDirty();
      }
    }
  ),

  renderAction: curry(({ store }, dataSourceItem: any) => {
    return <GridActionColumn relationshipDetails={dataSourceItem} store={store} />;
  }),

  renderComparision: curry(({ store }, dataSourceItem: any, key: string) => {
    const { isAllQuerySourceUncategorized } = store;
    const trimTimeInJoins = AppGlobal.advancedSettings.other.trimTimeInJoinsValue;

    // Get options list
    let options = !!dataSourceItem.isKeyJoin
      ? constants.COMPARISION_OPTIONS
      : filter({ forRelationship: true }, constants.COMPARISION_OPTIONS);


    // Filter option by data type
    const joinField = getJoinField(dataSourceItem, store);
    if (joinField) {
      options = filter(
        option =>
          option.DATA_TYPES.indexOf(joinField.izendaDataType) > -1 &&
          (!trimTimeInJoins || option.type !== constants.COMPARISION_TYPE.DATE_TIME),
        options
      );
    }

    function onChangeComparison(value) {
      const currentComparisionType = findComparisonType(dataSourceItem.comparisonOperator);
      const newComparisionType = findComparisonType(value);

      dataSourceItem.setComparisionOperator && dataSourceItem.setComparisionOperator(value);
      // Reset comparision value when change to another type
      if (currentComparisionType !== newComparisionType) {
        dataSourceItem.setComparisionValue(null);

        // When user switch back to field comparision, set correct data source category
        let categoryDataSource = constants.EMPTY_GUID;
        if (newComparisionType === constants.COMPARISION_TYPE.FIELD && isAllQuerySourceUncategorized) {
          categoryDataSource = EMPTY_GUID;
        }

        // IF we choose filter comparison but don't have alias yet: we need to generate it
        if (newComparisionType === constants.COMPARISION_TYPE.FILTER) {
          const existingKeyJoins = store.dataSourceDetail.displayDataSourceGridKeyJoins;
          const existingKeyJoinAliases = map(x => x.foreignFilterAlias, existingKeyJoins);
          const aliasBase = dataSourceItem.joinFieldName + '_KeyJoin';
          let currentAlias = aliasBase;
          let i = 1;
          while (includes(currentAlias, existingKeyJoinAliases)) {
            currentAlias = aliasBase + i;
            i++;
          }
          dataSourceItem.foreignFilterAlias = currentAlias;
        }

        dataSourceItem.setForeignDataSourceCategory && dataSourceItem.setForeignDataSourceCategory(categoryDataSource);
      }
      store.dataSourceDetail.markDirty();
      store.dataSourceDetail.markDataSourceGridDirty();
    }

    return (
      <DropDown
        groupBy='group'
        options={options}
        value={dataSourceItem[key]}
        onChange={EventUtils.notifyChange(onChangeComparison, EventUtils.DROPDOWN)}
        disabledBlankOption
        disabled={!dataSourceItem.joinFieldId}
        field={key}
        group={'Relationships' + dataSourceItem.tempId}
        showTooltip
      />
    );
  })
});



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/DataSource/Izenda.DataSourceGridAdvancedActions.js